// Read more close logic
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-js-readmore-close]').forEach(button => {
    button.addEventListener('click', () => {
      button.closest('details')?.removeAttribute('open');
    });
  });
});

class EmblaSlider extends HTMLElement {
  constructor() {
    super();
    this.embla = null;
    this.cleanupProgress = null;
    this.debounceTimeout = null;
    this.hasInitialised = false;
  }

  connectedCallback() {
    const isLazy = this.hasAttribute('data-lazy');

    if (!isLazy) {
      this.initSlider();
      return;
    }

    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          console.log("initSlider - lazy");
          this.initSlider();
          observer.unobserve(this);
        }
      });
    }, {
      rootMargin: '0px 0px 200px 0px',
      threshold: 0.1,
    });

    observer.observe(this)
  }

  initSlider() {
    if (this.hasInitialised) return;
    this.hasInitialised = true;

    const tabButtons = this.querySelectorAll('[data-js-collection-tab]');
    if (tabButtons.length > 0) this.setupTabs(tabButtons);
    this.setupEmbla();
  }

  disconnectedCallback() {
    if (this.embla) this.embla.destroy();
    if (this.cleanupProgress) this.cleanupProgress();
  }

  setupTabs(tabButtons) {
    tabButtons.forEach(button => {
      button.addEventListener('click', () => {
        const handle = button.getAttribute('data-js-collection-tab');

        tabButtons.forEach(btn => btn.classList.remove('tab-button--active'));
        button.classList.add('tab-button--active');
        
        if (handle) this.loadCollectionFromTemplate(handle);
      });
    });
  }

  loadCollectionFromTemplate(handle) {
    const container = this.querySelector('[data-js-embla-container]');
    const template = this.querySelector(`[data-js-product-card-template="${handle}"]`);

    if (!template || !container) {
      console.warn('Missing template or embla container');
      return;
    }

    const cloned = template.content.cloneNode(true);
    container.innerHTML = '';
    container.appendChild(cloned);

    if (this.embla) this.embla.destroy();
    if (this.cleanupProgress) this.cleanupProgress();

    this.setupEmbla();
  }

  setupEmbla() {
    const emblaNode = this.querySelector('[data-js-embla]');
    const emblaContainer = emblaNode?.querySelector('[data-js-embla-container]');
    const prevBtn = this.querySelector('[data-js-embla-prev]');
    const nextBtn = this.querySelector('[data-js-embla-next]');
    const progressBar = this.querySelector('[data-js-embla-progress]');

    if (!window.EmblaCarousel) {
      console.error("EmblaCarousel not loaded??");
      return;
    }

    if (!emblaNode || !emblaContainer) {
      console.error("missing  nodes");
      return;
    }

    const loop = this.getAttribute('data-loop') === 'true';
    const align = this.getAttribute('data-align') || 'start';
    const skipSnaps = this.getAttribute('data-skip-snaps') !== 'false';

    this.embla = EmblaCarousel(emblaNode, { loop, align, skipSnaps });

    const toggleButtons = () => {
      const canScrollPrev = this.embla.canScrollPrev();
      const canScrollNext = this.embla.canScrollNext();
      if (prevBtn) prevBtn.disabled = !canScrollPrev;
      if (nextBtn) nextBtn.disabled = !canScrollNext;
    };

    if (prevBtn) prevBtn.addEventListener('click', () => this.embla.scrollPrev());
    if (nextBtn) nextBtn.addEventListener('click', () => this.embla.scrollNext());

    this.embla.on('select', toggleButtons);
    this.embla.on('init', toggleButtons);
    toggleButtons();

    if (progressBar) {
      const updateProgress = () => {
        const raw = this.embla.scrollProgress();
        const clamped = Math.min(1, Math.max(0, raw));
        const percentage = (clamped * 100).toFixed(2);
        progressBar.style.width = `${percentage}%`;
      };

      const debouncedUpdate = () => {
        clearTimeout(this.debounceTimeout);
        this.debounceTimeout = setTimeout(updateProgress, 16);
      };

      this.embla.on('scroll', debouncedUpdate);
      this.embla.on('reInit', updateProgress);
      progressBar.style.width = '0%';
      updateProgress();

      this.cleanupProgress = () => {
        clearTimeout(this.debounceTimeout);
        this.embla.off('scroll', debouncedUpdate);
        this.embla.off('reInit', updateProgress);
      };
    }
  }
}

customElements.define('embla-slider', EmblaSlider);

class AnimatedDetails extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.details = this.querySelector('details');
    this.summary = this.details?.querySelector('summary');
    this.content = this.details?.querySelector('details>div');
    // Alex - this only works with specific plus sign which has 2 paths, 1st being vertical line
    this.plusIcon = this.details?.querySelector('[data-js-plus-icon]');

    if (typeof window.Motion !== 'object' || typeof window.Motion.animate !== 'function') {
      console.error('[AnimatedDetails] Motion One is not loaded or unavailable.');
      return;
    }

    if (!this.details || !this.summary || !this.content) return;

    this.details.removeAttribute('open');
    this.content.style.overflow = 'hidden';
    this.content.style.opacity = 0;
    this.content.style.height = '0px';
    this.content.style.display = 'none';

    this.summary.addEventListener('click', (e) => {
      e.preventDefault();

      const isOpen = this.details.hasAttribute('open');

      if (isOpen) {
        if (this.plusIcon) this.plusIcon.querySelector('svg>path:first-child').style.opacity = 1;
        Motion.animate(this.content, {
          opacity: [1, 0],
          height: [`${this.content.scrollHeight}px`, '0px']
        }, {
          duration: 0.2,
          easing: 'ease-in'
        }).finished.then(() => {
          this.content.style.display = 'none';
          this.details.removeAttribute('open');
        });
      } else {
        if (this.plusIcon) this.plusIcon.querySelector('svg>path:first-child').style.opacity = 0;
        this.details.setAttribute('open', '');
        this.content.style.display = 'block';
        Motion.animate(this.content, {
          opacity: [0, 1],
          height: ['0px', `${this.content.scrollHeight}px`]
        }, {
          duration: 0.2,
          easing: 'ease-out'
        });
      }
    });
  }
}

customElements.define('animated-details', AnimatedDetails);

class AnimatedReadMore extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    if (typeof window.Motion !== 'object' || typeof window.Motion.animate !== 'function') {
      console.error('[AnimatedReadMore] Motion One not available.');
      return;
    }

    this.content = this.querySelector('[data-js-read-more-content]');
    this.button = this.querySelector('[data-js-read-more-button]');
    if (!this.content || !this.button) return;

    this.expanded = false;
    this.maxHeight = parseInt(this.dataset.maxHeight || 100, 10);

    // Only apply initial collapsed state if not already present
    const initialMaxHeight = parseInt(this.content.style.maxHeight, 10);
    if (isNaN(initialMaxHeight) || initialMaxHeight > this.maxHeight) {
      this.content.style.maxHeight = `${this.maxHeight}px`;
    }

    if (!this.content.classList.contains('mask-fade-bottom')) {
      this.content.classList.add('mask-fade-bottom');
    }

    if (this.content.style.overflow !== 'hidden') {
      this.content.style.overflow = 'hidden';
    }

    this.button.addEventListener('click', () => this.toggle());
  }

  toggle() {
    const content = this.content;

    this.expanded = !this.expanded;
    const targetHeight = this.expanded ? content.scrollHeight : this.maxHeight;

    if (this.expanded) {
      content.classList.remove('mask-fade-bottom');
    }

    Motion.animate(content, {
      maxHeight: [content.offsetHeight, targetHeight]
    }, {
      duration: 0.4,
      easing: 'ease-in-out',
    }).finished.then(() => {
      if (this.expanded) {
        content.style.maxHeight = 'none'; // allow natural flow
      } else {
        content.style.maxHeight = `${this.maxHeight}px`;
        content.classList.add('mask-fade-bottom');
      }
    });

    this.button.textContent = this.expanded ? 'Show less' : 'Read more';
  }
}

customElements.define('animated-read-more', AnimatedReadMore);

// Customised version of "menu-drawer" used for mobile collection filter/sort drawer
// Note: original used to extend for "header drawer" - so leave that alone, I might want to keep the animations in there
class WmMenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.js-has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    // The main drawer opening
    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    // The detail/summary opening
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      // FIXME: see if this causes problem, forcing this to stay open
      // details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('wm-menu-drawer', WmMenuDrawer);

// Simplified Slideshow Component
class WmSlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('[data-js-slider-buttons]');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('[data-js-slideshow-slide]');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('[data-js-slide-counter-link]'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('[data-js-slide-counter-link]');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('active-dot');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('active-dot');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('wm-slideshow-component', WmSlideshowComponent);

customElements.define('product-card-carousel', class ProductCardCarousel extends HTMLElement {
  constructor() {
    super();
    this.slideWidth = 280;
    this.spaceBetween = 8;
  }

  connectedCallback() {
    this.carousel = this.querySelector('.js-product-carousel');
    this.prevBtn = this.querySelector('.js-previous-button');
    this.nextBtn = this.querySelector('.js-next-button');
    this.counter = this.querySelector('.js-counter');
    this.totalSlidesElement = this.querySelector('.js-total-slides');
    this.slides = Array.from(this.carousel.children);
    this.totalSlides = this.slides.length;
    
    this.totalSlidesElement.textContent = this.totalSlides;
    
    this.prevBtn.addEventListener('click', () => this.navigateSlide(-1));
    this.nextBtn.addEventListener('click', () => this.navigateSlide(1));
    
    this.scrollHandler = this.debounce(() => this.updateCounterBasedOnScroll(), 50);
    this.carousel.addEventListener('scroll', this.scrollHandler);
    
    this.updateButtonState();
    
    this.resizeObserver = new ResizeObserver(() => this.updateCounterBasedOnScroll());
    this.resizeObserver.observe(this.carousel);
  }
  
  disconnectedCallback() {
    // Clean up event listeners
    this.carousel.removeEventListener('scroll', this.scrollHandler);
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }
  
  debounce(func, wait) {
    let timeout;
    return function() {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this), wait);
    };
  }
  
  updateCounterBasedOnScroll() {
    const carouselRect = this.carousel.getBoundingClientRect();
    const rightEdgePosition = carouselRect.right;
    
    let rightmostVisibleIndex = 0;
    
    // Use binary search for better performance with large number of slides
    if (this.slides.length > 20) {
      rightmostVisibleIndex = this.findRightmostVisibleIndexBinary(rightEdgePosition);
    } else {
      // Original linear search for small collections
      for (let i = 0; i < this.slides.length; i++) {
        const slideRect = this.slides[i].getBoundingClientRect();
        if (slideRect.right <= rightEdgePosition + 1) {
          rightmostVisibleIndex = i;
        } else {
          break;
        }
      }
    }
    
    this.counter.textContent = rightmostVisibleIndex + 1;
    this.updateButtonState(rightmostVisibleIndex);
  }
  
  findRightmostVisibleIndexBinary(rightEdgePosition) {
    let low = 0;
    let high = this.slides.length - 1;
    let result = 0;
    
    while (low <= high) {
      const mid = Math.floor((low + high) / 2);
      const slideRect = this.slides[mid].getBoundingClientRect();
      
      if (slideRect.right <= rightEdgePosition + 1) {
        result = mid; // This slide is visible, but there might be more
        low = mid + 1;
      } else {
        high = mid - 1;
      }
    }
    
    return result;
  }
  
  navigateSlide(direction) {
    const currentScroll = this.carousel.scrollLeft;
    const newScroll = currentScroll + (direction * (this.slideWidth + this.spaceBetween));
    
    this.carousel.scrollTo({
      left: newScroll,
      behavior: 'smooth'
    });
  }
  
  updateButtonState(rightmostVisibleIndex) {
    const currentIndex = rightmostVisibleIndex ?? parseInt(this.counter.textContent) - 1;
    
    // Check if the first slide is visible
    const firstSlideRect = this.slides[0].getBoundingClientRect();
    const carouselRect = this.carousel.getBoundingClientRect();
    
    // Disable the back button if the first slide is visible
    this.prevBtn.disabled = firstSlideRect.left >= carouselRect.left;

    this.nextBtn.disabled = currentIndex >= this.totalSlides - 1;
  }
});